import { GeneratedCollection } from "../generated.collection.abstraction";
import { TransactionView } from "../../../models/view/transaction/transaction.view";
import { LogService } from "../../../../../../../libs/common/src/abstractions/log.service";
import { inject, Signal, signal, WritableSignal } from "@angular/core";
import { UserStoreService } from "../user/user.store.service";
import { Observable } from "rxjs";
import { toObservable } from "@angular/core/rxjs-interop";
import { TransactionAmountsTime } from "@bitwarden/web-vault/app/models/types/budget.types";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { sortTransactionViews } from "@bitwarden/web-vault/app/shared/utils/helper.transactionView/sort";

/**
 * BudgetTransactionGeneratedCollection - Generated collection of transactions for calculating budgets
 * It includes transactions limited by a date range. Then it stores these transactions grouped by categories
 * and their amounts.
 *
 * This will be triggered to update when the enriched transaction collection in TransactionStoreService changes.
 * In the future, when we have date selections for budgets, we can also trigger the controls to update this collection.
 *
 */
export class BudgetTransactionGeneratedCollection extends GeneratedCollection<TransactionView> {
  protected log: LogService = inject(LogService);
  protected userStore: UserStoreService = inject(UserStoreService);
  protected transactionStore = inject(TransactionStoreService);

  protected _collection: WritableSignal<TransactionView[]> = signal<TransactionView[]>(null);
  collection: Signal<TransactionView[]> = this._collection.asReadonly();
  collection$: Observable<TransactionView[]> = toObservable(this.collection);

  // Map of category ID to array of TransactionAmountsTime
  protected _sortedCollection: WritableSignal<Map<string, TransactionAmountsTime[]>> =
    signal<Map<string, TransactionAmountsTime[]>>(null);
  sortedCollection: Signal<Map<string, TransactionAmountsTime[]>> =
    this._sortedCollection.asReadonly();
  sortedCollection$: Observable<Map<string, TransactionAmountsTime[]>> = toObservable(
    this.sortedCollection,
  );

  /**
   * Generate the collection of transactions for budget calculations
   * If startDate is not provided, all enriched transactions are included
   * For phase 1 all the transactions are included until we start using filters
   *
   * @param startDate - set as null for phase 1 to include all transactions
   */
  generate(startDate?: Date): void {
    let filteredTransactions = [...this.transactionStore.enrichTransaction.collection()];

    if (startDate) {
      // get the starting time of the provided date
      const startOfDay = new Date(startDate);
      startOfDay.setHours(0, 0, 0, 0);
      const startDateTime = startOfDay.getTime();

      filteredTransactions =
        filteredTransactions?.filter((t) => {
          // only include transactions on or after the start date
          const transactionDate = new Date(t.postDate).getTime();
          return transactionDate >= startDateTime;
        }) || [];
    }

    filteredTransactions.sort(sortTransactionViews);
    this.update(filteredTransactions);
  }

  /**
   * Generate the sorted collection mapping category IDs to their transaction amounts and timestamps
   * Once this has been generated, we can use it for all budget calculations to make it more efficient
   */
  generateSortedCollection(): void {
    const transactions = this._collection();
    if (!transactions) {
      this._sortedCollection.set(null);
      return;
    }

    const transactionsMap = new Map<string, TransactionAmountsTime[]>();

    for (const transaction of transactions) {
      // sum all the allocations for transactions by the category ID across all classifications
      const transactionAmounts: Record<string, number> = {};
      const allocations =
        transaction.c_allocationsNormalized ?? transaction.c_allocationsNested ?? {};

      for (const classificationId in allocations) {
        const allocationAmounts = allocations[classificationId] ?? {};
        for (const categoryId in allocationAmounts) {
          const allocationAmount = allocationAmounts[categoryId];

          if (!transactionAmounts[categoryId]) {
            transactionAmounts[categoryId] = allocationAmount;
          } else {
            transactionAmounts[categoryId] += allocationAmount;
          }
        }
      }

      for (const categoryId in transactionAmounts) {
        const transactionAmountTime: TransactionAmountsTime = {
          amount: transactionAmounts[categoryId],
          timeStamp: transaction.postDate.getTime(),
        };

        if (!transactionsMap.has(categoryId)) {
          transactionsMap.set(categoryId, []);
        }
        transactionsMap.get(categoryId).push(transactionAmountTime);
      }
    }

    this._sortedCollection.set(transactionsMap);
  }

  update(collection: TransactionView[]): void {
    this._collection.set([...collection]);
    this.generateSortedCollection();
  }

  clear(): void {
    this._collection.set(null);
    this._sortedCollection.set(null);
  }
}
